import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${window.__BACKEND_URL__}/api/casting-drafts`;
const options = { withCredentials: true, timeout: 30000 };

// A role-specific component instance prevents old responses crossing identities.
export default function CastingDraftTools(props) {
  const { user } = useAuth();
  const identity = `${user?.id || ''}:${user?.active_role || user?.role || ''}`;
  return <DraftTools key={`${identity}:${props.editor}:${props.resetKey || 0}`} {...props} />;
}

function DraftTools({ editor, snapshot, title, onRestore, onDirtyChange, allowNavigationRef }) {
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState(null);
  const [rows, setRows] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(true);
  const serialised = JSON.stringify(snapshot);
  const initial = useRef(serialised);
  const dirty = (saved ?? initial.current) !== serialised;
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      if (allowNavigationRef?.current) return;
      event.preventDefault(); event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, allowNavigationRef]);

  const fail = (err) => {
    if (alive.current) setError(typeof err?.response?.data?.detail === 'string'
      ? err.response.data.detail : 'Draft could not be saved or opened. Please try again.');
  };
  const save = async () => {
    setBusy(true); setError('');
    const sent = serialised;
    try {
      const body = { editor, title: String(title || 'Untitled casting').slice(0, 200),
        state: JSON.parse(sent), version: draft?.version || 0 };
      const { data } = draft
        ? await axios.put(`${API}/${draft.draft_id}`, body, options)
        : await axios.post(API, body, options);
      if (alive.current) { setDraft(data); setSaved(sent); setRows(null); }
    } catch (err) { fail(err); }
    finally { if (alive.current) setBusy(false); }
  };
  const list = async (nextOffset = 0) => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.get(API, { ...options, params: { editor, offset: nextOffset } });
      if (alive.current) { setRows(data.drafts); setOffset(nextOffset); setHasMore(data.has_more); }
    } catch (err) { fail(err); }
    finally { if (alive.current) setBusy(false); }
  };
  const open = async (id) => {
    if (!window.confirm('Opening a saved draft replaces the current editor contents. Save any changes first. Continue?')) return;
    setBusy(true); setError('');
    try {
      const { data } = await axios.get(`${API}/${id}`, options);
      if (alive.current && data.editor === editor) {
        onRestore(data.state); setDraft(data); setSaved(JSON.stringify(data.state)); setRows(null);
      }
    } catch (err) { fail(err); }
    finally { if (alive.current) setBusy(false); }
  };
  return (
    <section aria-label="Private casting drafts" className="rounded-xl border border-theme bg-theme-bg-secondary p-3 text-sm text-theme-text">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={busy} onClick={save} className="min-h-11 rounded-lg bg-[#002FA7] px-4 font-semibold text-white disabled:opacity-50">{busy ? 'Please wait…' : 'Save draft'}</button>
        <button type="button" disabled={busy} onClick={() => list()} className="min-h-11 rounded-lg border border-theme px-4">Open saved drafts</button>
        <span role="status" className="text-xs text-theme-muted">{dirty ? 'Unsaved changes' : saved ? 'Draft saved privately' : 'Save an incomplete draft and finish it later. Nothing is published.'}</span>
      </div>
      {error && <p role="alert" className="mt-2 text-red-500">{error}</p>}
      {rows && <div className="mt-3 space-y-2">
        {!rows.length && <p>No saved drafts on this page.</p>}
        {rows.map((row) => <button type="button" key={row.draft_id} disabled={busy} onClick={() => open(row.draft_id)} className="block min-h-11 w-full rounded-lg border border-theme px-3 py-2 text-left break-words">{row.title || 'Untitled casting'}<span className="ml-2 text-xs text-theme-muted">{new Date(row.updated_at).toLocaleString('en-GB')}</span></button>)}
        <div className="flex gap-3">
          {offset > 0 && <button type="button" disabled={busy} onClick={() => list(Math.max(0, offset - 20))}>Previous</button>}
          {hasMore && <button type="button" disabled={busy} onClick={() => list(offset + 20)}>Next</button>}
          <button type="button" onClick={() => setRows(null)}>Close drafts</button>
        </div>
      </div>}
    </section>
  );
}
